import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store from "../Store";
import Datastore from "../../communication/Datastore";
import { Timetable } from "../../types/archive/Timetable";

// Define the type for our state store entry
type TimetableState = {
    timetables: Timetable[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: TimetableState = {
    timetables: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const timetableSlice = createSlice({
    name: "timetable",
    initialState: initialState,
    reducers: {
        resetTimetableStatus(state) {
            state.status = "idle";
        },
        removeTimetables: (state, action) => {
            state.timetables = state.timetables.filter(
                item => !action.payload.some(id => id === item.id),
            );
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    timetables:
                        rawData?.payload?.timetable?.timetables?.map(
                            c => new Timetable(c),
                        ) || [],
                    status: !rawData?.payload?.timetable?.timetables?.length
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchPublishedTimetables.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchPublishedTimetables.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.timetables = action.payload.map(c => new Timetable(c));
            })
            .addCase(fetchPublishedTimetables.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetTimetableStatus, removeTimetables } = timetableSlice.actions;

// This is a thunk, they are used to load the state
export const fetchPublishedTimetables = createAsyncThunk(
    "timetable/fetchPublishedTimetables",
    () => Datastore.getType<Timetable[]>("api/Timetables/Published/Files"),
    {
        condition: () => {
            const { timetable } = store.getState().db;
            return timetable.status === "idle";
        },
    },
);
export const initialiseTimetableDetails = async (): Promise<void> => {
    await store.dispatch(fetchPublishedTimetables());
};
export const reinitialiseTimetableDetails = async (): Promise<void> => {
    store.dispatch(resetTimetableStatus());
    await store.dispatch(fetchPublishedTimetables());
};
export const clearTimetableDetails = () => {
    store.dispatch(resetTimetableStatus());
};
export const deleteTimetables = createAsyncThunk(
    "timetables/deleteTimetables",
    async (timetables: Timetable[], thunkAPI) => {
        await Datastore.post(`api/SystemIO/Files/Recycle`, timetables);

        await thunkAPI.dispatch(removeTimetables(timetables.map(tt => tt.id)));
    },
);

// We have to export the reducer as this is added to the state store
export default timetableSlice.reducer;
